/**
 * Workflow Engine Hook
 *
 * Initializes the workflow engine after user authentication.
 * This ensures all notifications, workflows, and polling are active.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  initWorkflowEngine,
  shutdownWorkflowEngine,
  useNotificationsStore,
  testCustomEmail,
  testEmailClassificationWorkflow,
} from '@business-assistant/shared';

// Expose test functions globally for console testing
declare global {
  interface Window {
    testEmail: typeof testCustomEmail;
    testAllEmails: typeof testEmailClassificationWorkflow;
  }
}

// Make test functions available in browser console
if (typeof window !== 'undefined') {
  window.testEmail = testCustomEmail;
  window.testAllEmails = testEmailClassificationWorkflow;
}

export function useWorkflowEngine() {
  const { isAuthenticated, user } = useAuth();
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only initialize once when authenticated
    if (!isAuthenticated || !user || initializedRef.current) {
      return;
    }

    // Get notifications store functions
    const notificationsStore = useNotificationsStore.getState();

    // Initialize the workflow engine
    initWorkflowEngine({
      pushNotification: notificationsStore.addNotification,
      isEnabledForInApp: (type) => notificationsStore.isChannelEnabledForType(type, 'inApp'),
      isInDnD: () => notificationsStore.isInDoNotDisturbPeriod(),
      currentUserId: user.id,
      enablePolling: true, // Enable email polling
      enableEmailClassification: true, // Enable AI email classification
    });

    initializedRef.current = true;
    console.log('[useWorkflowEngine] Workflow engine initialized for user:', user.id);

    // Cleanup on unmount
    return () => {
      if (initializedRef.current) {
        shutdownWorkflowEngine();
        initializedRef.current = false;
        console.log('[useWorkflowEngine] Workflow engine shut down');
      }
    };
  }, [isAuthenticated, user]);
}
